import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, FileCode2, Loader2, RefreshCw, X } from "lucide-react";
import {
  FileTree,
  FileTreeFile,
  FileTreeFolder,
} from "@/components/ai-elements/file-tree";
import { Button } from "@/components/ui/button";
import {
  api,
  type FileContentPayload,
  type FileTreeNode,
} from "@/lib/api";
import { findSession, useStore } from "@/lib/store";
import { cn } from "@/lib/utils";

function formatBytes(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function TreeNodes({ nodes }: { nodes: FileTreeNode[] }) {
  return (
    <>
      {nodes.map((node) =>
        node.type === "dir" ? (
          <FileTreeFolder key={node.path} path={node.path} name={node.name}>
            {node.children && node.children.length > 0 ? (
              <TreeNodes nodes={node.children} />
            ) : (
              <div className="px-2 py-1 text-[11px] text-muted-foreground">empty</div>
            )}
          </FileTreeFolder>
        ) : (
          <FileTreeFile key={node.path} path={node.path} name={node.name} />
        ),
      )}
    </>
  );
}

function FilePreview({
  file,
  loading,
  error,
}: {
  file: FileContentPayload | null;
  loading: boolean;
  error: string | null;
}) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 p-4 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading file…
      </div>
    );
  }
  if (error) {
    return (
      <div className="m-3 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive/90">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span className="break-words">{error}</span>
      </div>
    );
  }
  if (!file) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
        <FileCode2 className="h-6 w-6" />
        <span className="text-xs">Select a file to preview</span>
      </div>
    );
  }
  if (file.binary) {
    return (
      <div className="p-4 text-xs text-muted-foreground">
        Binary file ({formatBytes(file.size)}) — no preview.
      </div>
    );
  }
  const lines = file.content.split("\n");
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center gap-2 border-b border-border/40 px-3 py-1.5">
        <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-muted-foreground">
          {file.path}
        </span>
        <span className="shrink-0 font-mono text-[10px] text-muted-foreground/70">
          {formatBytes(file.size)}
          {file.truncated && " (truncated)"}
        </span>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        <pre className="p-3 font-mono text-xs leading-5 text-foreground/90">
          {lines.map((line, i) => (
            <div key={i} className="flex">
              <span className="w-10 shrink-0 select-none pr-3 text-right text-muted-foreground/50">
                {i + 1}
              </span>
              <span className="whitespace-pre">{line}</span>
            </div>
          ))}
        </pre>
      </div>
    </div>
  );
}

export function FileExplorerPanel({ onClose }: { onClose: () => void }) {
  const state = useStore();
  const { source, slug, sessionId } = state.selected;
  const session = useMemo(
    () => findSession(state, source, slug, sessionId),
    [state, source, slug, sessionId],
  );
  const isLocal = source === "local" && !!sessionId;

  const [treeLoading, setTreeLoading] = useState(false);
  const [treeError, setTreeError] = useState<string | null>(null);
  const [nodes, setNodes] = useState<FileTreeNode[]>([]);
  const [root, setRoot] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);

  const [selectedPath, setSelectedPath] = useState<string | undefined>(undefined);
  const [file, setFile] = useState<FileContentPayload | null>(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  // Monotonic id per content request; a response only lands if it is still the
  // latest, so rapid selections (or a session switch) can't resurface a stale
  // preview.
  const fileReqRef = useRef(0);

  const loadTree = useCallback(async () => {
    if (!isLocal || !sessionId) return;
    setTreeLoading(true);
    setTreeError(null);
    try {
      const payload = await api.getFileTree({ sessionId, slug: slug ?? undefined });
      setNodes(payload.entries);
      setRoot(payload.root);
      setTruncated(payload.truncated);
    } catch (e) {
      setTreeError(e instanceof Error ? e.message : String(e));
    } finally {
      setTreeLoading(false);
    }
  }, [isLocal, sessionId, slug]);

  useEffect(() => {
    fileReqRef.current++;
    setNodes([]);
    setRoot(null);
    setSelectedPath(undefined);
    setFile(null);
    setFileError(null);
    setFileLoading(false);
    void loadTree();
  }, [loadTree]);

  const handleSelect = useCallback(
    (path: string, type: "file" | "dir") => {
      setSelectedPath(path);
      if (type !== "file" || !sessionId) return;
      const reqId = ++fileReqRef.current;
      setFileLoading(true);
      setFileError(null);
      api
        .getFileContent({ sessionId, slug: slug ?? undefined, path })
        .then((payload) => {
          if (fileReqRef.current !== reqId) return;
          setFile(payload);
          setFileLoading(false);
        })
        .catch((e) => {
          if (fileReqRef.current !== reqId) return;
          setFile(null);
          setFileError(e instanceof Error ? e.message : String(e));
          setFileLoading(false);
        });
    },
    [sessionId, slug],
  );

  // The FileTree onSelect only passes the path; classify it against the tree.
  const fileType = useCallback(
    (path: string): "file" | "dir" => {
      const walk = (list: FileTreeNode[]): FileTreeNode | undefined => {
        for (const n of list) {
          if (n.path === path) return n;
          if (n.children) {
            const hit = walk(n.children);
            if (hit) return hit;
          }
        }
        return undefined;
      };
      return walk(nodes)?.type ?? "file";
    },
    [nodes],
  );

  return (
    <div className="fixed bottom-0 left-0 right-0 z-30 flex h-[min(520px,78vh)] flex-col border-t border-border bg-card shadow-xl pb-[env(safe-area-inset-bottom)]">
      <div className="flex items-center justify-between border-b border-border/40 px-4 py-3">
        <div className="min-w-0 flex items-center gap-2">
          <h2 className="shrink-0 text-sm font-semibold">Files</h2>
          {root && (
            <span className="min-w-0 truncate font-mono text-[11px] text-muted-foreground">
              {root}
            </span>
          )}
          {truncated && (
            <span className="shrink-0 rounded border border-amber-400/40 bg-amber-400/10 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-amber-400">
              partial
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => void loadTree()}
            disabled={treeLoading || !isLocal}
            aria-label="Refresh files"
          >
            <RefreshCw className={cn("h-4 w-4", treeLoading && "animate-spin")} />
          </Button>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary/50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {!isLocal ? (
        <div className="flex flex-1 items-center justify-center p-6 text-center">
          <div className="max-w-sm text-xs text-muted-foreground">
            {session
              ? "File browsing is only available for sessions on this machine. Remote (hub) sessions are not supported yet."
              : "Select a local session to browse its working directory."}
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1">
          <div className="w-[280px] shrink-0 overflow-auto border-r border-border/40 sm:w-[320px]">
            {treeLoading && nodes.length === 0 && (
              <div className="flex items-center gap-2 p-4 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading tree…
              </div>
            )}
            {treeError && (
              <div className="m-3 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive/90">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span className="break-words">{treeError}</span>
              </div>
            )}
            {!treeError && nodes.length > 0 && (
              <FileTree
                className="rounded-none border-0 bg-transparent text-xs"
                selectedPath={selectedPath}
                onSelect={(path) => handleSelect(path, fileType(path))}
              >
                <TreeNodes nodes={nodes} />
              </FileTree>
            )}
            {!treeLoading && !treeError && nodes.length === 0 && (
              <div className="p-4 text-xs text-muted-foreground">No files found.</div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <FilePreview file={file} loading={fileLoading} error={fileError} />
          </div>
        </div>
      )}
    </div>
  );
}
